import { useSearchStore } from '@stores/search.store'

import type { FormEvent, JSX } from 'react'

import dynamic from 'next/dynamic'

import type { JobDescription, JobSearch, Location } from '@types'

import { useShallow } from 'zustand/react/shallow'

import Button from '@components/buttons/button/button'
import Checkbox from '@components/inputs/checkbox'
import useJobSearch from '@hooks/useJobSearch'
import { searchSubmittedEvent } from '@lib/analytics-events'
import { miToKm } from '@lib/helpers/client'

import styles from './form.module.scss'

const JobTypeAhead = dynamic(() => import('@components/inputs/typeahead/job-typeahead'))
const LocationTypeAhead = dynamic(() => import('@components/inputs/typeahead/location-typeahead'))

export default function Form({ close }: { close: () => void }): JSX.Element {
  const { submit, errors, setError } = useJobSearch()
  const [nextSearch, addParamsToSearch] = useSearchStore(
    useShallow((state) => [state.nextSearch, state.addParamsToSearch]),
  )

  const onFormSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    const defaultFilters: Partial<JobSearch> = {
      salary: { type: 'PER_ANNUM', min: 0 },
      maxRadius: miToKm(25),
      remote: false,
      createdAfter: '',
      sort: 'FEATURED',
    }
    searchSubmittedEvent({ ...nextSearch, ...defaultFilters }, 'Search')
    submit(defaultFilters)

    if (!nextSearch.location.displayName || !nextSearch.job.micro) {
      return
    }

    close()
  }

  return (
    <form className={styles.form} onSubmit={onFormSubmit}>
      <div className={styles.typeaheads}>
        <JobTypeAhead
          className={styles.job}
          currentJob={nextSearch.job}
          setJob={(jobDescription: JobDescription): void => {
            addParamsToSearch({ job: jobDescription })
          }}
          showError={errors.job}
          setShowError={(v: boolean): void => setError(v, 'job')}
        />
        <LocationTypeAhead
          className={styles.location}
          currentLocation={nextSearch.location}
          setLocation={(locationDescription: Location): void => {
            addParamsToSearch({ location: locationDescription })
          }}
          showError={errors.location}
          setShowError={(v: boolean): void => setError(v, 'location')}
        />
      </div>
      <div className={styles.bottom}>
        <div className={styles.filters}>
          <Checkbox
            label="Full-time"
            id="fulltime"
            checked={nextSearch.fullTime}
            onChange={(e): void => addParamsToSearch({ fullTime: e.target.checked })}
          />
          <Checkbox
            label="Part-time"
            id="parttime"
            checked={nextSearch.partTime}
            onChange={(e): void => addParamsToSearch({ partTime: e.target.checked })}
          />
          <Checkbox
            label="No experience required"
            id="no-experience"
            checked={nextSearch.noExperience}
            onChange={(e): void => addParamsToSearch({ noExperience: e.target.checked })}
          />
        </div>
        <Button variant="yellow" size="lg">
          Search
        </Button>
      </div>
    </form>
  )
}
